import { useGetEnums } from 'api-hooks/common';
import { GuestModel, useGetGuests } from 'api-hooks/guest';
import { Option } from 'baseui/select';
import { capitalize, transformLabel } from 'common/utils/string';
import { useFormState } from 'components/elements/form';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import Text from 'components/elements/text';
import {
  CardSection,
  FullContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { onAddNewGuestRelation } from '../../hooks/use-get-default-values';
import { GuestFormType, GuestRelationFormType } from '../form-type';

interface Props {
  guest?: GuestModel;
}

function transformer(item: any): Option {
  return {
    id: item.id,
    label: transformLabel([capitalize(item.title), item.name], ' '),
    extra: item,
  };
}

function enumTransformer(item: any): Option {
  return {
    id: item.value,
    label: item.label,
  };
}

function useGetGuestRelationTableList(
  relationOptions: Option[] = [],
  guestOptions: Option[] = [],
): IColumn<GuestRelationFormType>[] {
  const { t } = useTranslation();
  const { editable } = useFormState();

  return [
    {
      header: t('common:guest'),
      accessorKey: 'guestId',
      minSize: 250,
      dataType: ColumnDataType.select,
      options: guestOptions,
    },
    {
      header: t('guest:guest_relation'),
      accessorKey: 'type',
      minSize: 250,
      dataType: ColumnDataType.select,
      options: relationOptions,
    },
    ...(editable
      ? [
          {
            header: '',
            accessorKey: 'delete',
            size: 60,
            maxSize: 60,
            dataType: ColumnDataType.delete,
          },
        ]
      : []),
  ];
}

export default function GuestRelationCard(props: Props) {
  const { t } = useTranslation();

  const { control, getValues, clearErrors, trigger } =
    useFormContext<GuestFormType>();
  const { editable } = useFormState();

  const { data: dataRelationType } = useGetEnums({
    class: 'guest-relation-type',
  });

  const { data: dataGuests } = useGetGuests(
    {
      params: {
        limit: -1,
      },
    },
    {
      staleTime: Infinity,
    },
  );

  const name = 'relations' as const;

  const enumOptions = (dataRelationType?.data ?? []).map((option) =>
    enumTransformer(option),
  );

  const guestOptions = (dataGuests?.data ?? [])
    .filter((item) => {
      if (item.id === props.guest?.id) return false;
      return true;
    })
    .map((item) => transformer(item));

  const { fields, update, append, remove } = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });

  const isEmpty = fields.length === 0;

  const columns = useGetGuestRelationTableList(enumOptions, guestOptions);

  const dispatch = React.useCallback(
    (action) => {
      const relations = getValues(name);
      const currentRowIndex = action.rowIndex as number;
      const relation = relations[currentRowIndex];
      const type = action.type as ActionType;
      const columnId = action.columnId as keyof GuestRelationFormType;
      const value = action.value;

      const otherVal: () => Partial<GuestRelationFormType> = () => {
        return {};
      };

      if (type === 'addRow') {
        append(onAddNewGuestRelation());
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...relation,
          [columnId]: value,
          ...otherVal(),
        });
        clearErrors(`${name}.${currentRowIndex}.${columnId}`);
      }
    },
    [append, clearErrors, getValues, name, remove, trigger, update],
  );

  return (
    <CardSection>
      <Text variant="HeadingXSmall">{t('guest:guest_relation')}</Text>
      <SeparatorVertical $margin={16} />
      {isEmpty && !editable && (
        <Text variant="ParagraphLarge">{t('common:empty')}</Text>
      )}
      {(editable || !isEmpty) && (
        <FullContainer>
          <TableInput
            columns={columns}
            data={fields}
            dispatch={dispatch}
            disabled={!editable}
            uniqueRowKey="customId"
            name={name}
          />
        </FullContainer>
      )}
    </CardSection>
  );
}
